const REGISTRY_BUTTON_LOADING_TEXT = "מוסיף לרשימה...";
const REGISTRY_BUTTON_ADDED_TEXT = "נוסף לרשימה";

/**
 * @param {HTMLElement} button
 * @param {boolean} isLoading
 * @param  labelState
 */
function setRegistryButtonLoadingState(button, isLoading, { originalLabel, loadingText }) {
  const buttonLabel = button.querySelector('span');
  button.disabled = isLoading;
  if (isLoading) {
    button.setAttribute('aria-busy', 'true');
    if (buttonLabel && loadingText) {
      buttonLabel.textContent = loadingText;
    }
  } else {
    button.removeAttribute('aria-busy');
    if (buttonLabel) {
      buttonLabel.textContent = originalLabel;
    }
  }
}

async function postVariantToRegistry(registryId, variantId, quantity, gridBundleComposition) {
  const payload = {
    variantId: Number(variantId),
    registryId,
    quantity: Number(quantity),
  };
  if (gridBundleComposition) {
    payload.gridBundleComposition = gridBundleComposition;
  }
  const response = await fetch(`${REGISTRY_END_POINT}/${registryId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error('registry_post_failed');
  }
  const data = await response.json();
  if (!data.success) {
    throw new Error('registry_rejected');
  }
  window.BoaBabyRegistryBootstrap?.invalidatePrefetch?.();
  window.BoaBabyRegistryBootstrap?.clearLegacyRegistryCache?.();
  document.dispatchEvent(new CustomEvent('registry:count-changed'));
  document.dispatchEvent(new CustomEvent('registry:reload'));
  return data;
}

function parseVariantId(raw) {
  const id = Number(String(raw || '').trim());
  return Number.isFinite(id) && id > 0 ? id : null;
}

function usableBundleImageUrl(raw) {
  const url = String(raw || '').trim();
  return url && !url.includes('error') ? url : '';
}

function collectDropdownBundleSelections(bundleForm, parentVariantId) {
  if (!bundleForm?.dataset?.bundle) {
    return { composition: null, optionalIds: [] };
  }
  let bundleData;
  try {
    bundleData = JSON.parse(bundleForm.dataset.bundle);
  } catch {
    return { composition: null, optionalIds: [] };
  }
  const parentId = parseVariantId(parentVariantId);
  if (!parentId) {
    return { composition: null, optionalIds: [] };
  }

  const mandatoryIds = [];
  const optionalIds = [];
  const childLineImages = {};
  const parentImage = usableBundleImageUrl(bundleForm.dataset.mainProductImage);
  if (parentImage) {
    childLineImages[parentId] = parentImage;
  }
  for (const [key, value] of new FormData(bundleForm).entries()) {
    if (!value || String(value).trim() === '') {
      continue;
    }
    const id = parseVariantId(value);
    if (!id) {
      continue;
    }
    const optionImage = usableBundleImageUrl(
      bundleForm.querySelector(`[name="${key}"]`)?.selectedOptions?.[0]?.dataset?.mainImage,
    );
    if (optionImage) {
      childLineImages[id] = optionImage;
    }
    if (bundleData[`${key}_required`]) {
      mandatoryIds.push(id);
    } else {
      optionalIds.push(id);
    }
  }

  if (!mandatoryIds.length) {
    return { composition: null, optionalIds };
  }

  const title = bundleForm.dataset.bundleTitle?.trim() || '';
  return {
    composition: {
      compositionId: [parentId, ...mandatoryIds].join(':'),
      childVariantIds: [parentId, ...mandatoryIds],
      bundleVariantId: parseVariantId(bundleForm.dataset.bundleProductVariantId),
      ...(title ? { title } : {}),
      childLineImages,
    },
    optionalIds,
  };
}

function collectGridBundleComposition(bundleForm) {
  const parentVariantInput = document.getElementById('bundle-parent-variant');
  const parentVariantId = parseVariantId(parentVariantInput?.value);
  if (!parentVariantId) {
    return null;
  }

  const childVariantIds = [];

  bundleForm.querySelectorAll('.bundle-main-items .bundle-required-variant').forEach((input) => {
    const id = parseVariantId(input.value);
    if (id) {
      childVariantIds.push(id);
    }
  });

  if (!childVariantIds.length) {
    return null;
  }

  const pricingEl = document.getElementById('bundle-pricing-data');
  const title = pricingEl?.getAttribute('data-bundle-title')?.trim() || '';

  return {
    compositionId: [parentVariantId, ...childVariantIds].join(':'),
    childVariantIds,
    ...(title ? { title } : {}),
  };
}

function collectGridBundleOptionalAddonVariantIds(bundleForm, parentVariantId) {
  const ids = [];
  bundleForm.querySelectorAll('.bundle-addon-toggle.is-selected').forEach((btn) => {
    const id = parseVariantId(btn.getAttribute('data-bundle-variant-id'));
    if (id && id !== parentVariantId) {
      ids.push(id);
    }
  });
  return ids;
}

async function addGridBundleToRegistry(registryButton) {
  const registryId = registryButton.dataset.registryId;
  const bundleForm = document.getElementById('grid-bundle-form');
  const parentVariantInput = document.getElementById('bundle-parent-variant');
  const buttonLabel = registryButton.querySelector('span');
  const originalLabel = buttonLabel?.textContent || '';

  if (!registryId) {
    return;
  }

  if (!bundleForm || !parentVariantInput) {
    console.error('addGridBundleToRegistry: bundle form inputs missing');
    return;
  }

  if (typeof validateRequiredGridBundleProducts === 'function' && !validateRequiredGridBundleProducts()) {
    return;
  }

  const gridBundleComposition = collectGridBundleComposition(bundleForm);
  const variantId = parseVariantId(parentVariantInput.value);
  if (!variantId || !gridBundleComposition) {
    (document.getElementById('grid-bundle-error') || document.querySelector('.dropdown-bundle-error'))
      ?.classList.remove('hidden');
    return;
  }

  const optionalAddonIds = collectGridBundleOptionalAddonVariantIds(bundleForm, variantId);

  setRegistryButtonLoadingState(registryButton, true, {
    originalLabel,
    loadingText: REGISTRY_BUTTON_LOADING_TEXT,
  });

  try {
    await postVariantToRegistry(registryId, variantId, 1, gridBundleComposition);

    for (const vid of optionalAddonIds) {
      await postVariantToRegistry(registryId, vid, 1);
    }
    updateRegistryButtonOnSuccess(registryButton, originalLabel);
  } catch (error) {
    console.error('addGridBundleToRegistry', error);
    setRegistryButtonLoadingState(registryButton, false, { originalLabel });
  }
}

async function addProductToRegistry(event) {
  event.preventDefault();

  const trigger = event.currentTarget || event.target;
  const registryButton = trigger.closest('.btn-add-to-registry');
  const registryId = registryButton?.dataset?.registryId;

  if (!registryButton || !registryId) {
    return;
  }

  if (registryButton.dataset.gridBundleRegistry === 'true') {
    return addGridBundleToRegistry(registryButton);
  }

  const blockBuyButtons = trigger.closest('.block-buy-buttons');
  if (!blockBuyButtons) {
    return;
  }

  const quantityElement = blockBuyButtons.closest('.product-grid__content');
  const qtyInput = quantityElement?.querySelector('.js-qty__num');
  const quantity = qtyInput ? Number(qtyInput.value) : 1;
  const variantId = Number(registryButton.dataset.variantId);

  const buttonLabel = registryButton.querySelector('span');
  const originalLabel = buttonLabel?.textContent || '';
  setRegistryButtonLoadingState(registryButton, true, {
    originalLabel,
    loadingText: REGISTRY_BUTTON_LOADING_TEXT,
  });

  const bundleForm = document.getElementById('bundle-selection-form');
  if (bundleForm && typeof checkForDropdownBundleErrors === 'function' && !checkForDropdownBundleErrors()) {
    setRegistryButtonLoadingState(registryButton, false, { originalLabel });
    return;
  }

  try {
    const { composition, optionalIds } = bundleForm
      ? collectDropdownBundleSelections(bundleForm, variantId)
      : { composition: null, optionalIds: [] };
    const bundleVariantId = composition?.bundleVariantId || variantId;

    await postVariantToRegistry(registryId, bundleVariantId, quantity, composition);

    for (const vid of optionalIds.filter((id) => id !== variantId && id !== bundleVariantId)) {
      await postVariantToRegistry(registryId, vid, quantity);
    }
    updateRegistryButtonOnSuccess(registryButton, originalLabel);
  } catch (error) {
    console.error('addProductToRegistry', error);
    setRegistryButtonLoadingState(registryButton, false, { originalLabel });
  }
}

function bindAddToRegistryButtons() {
  const buttons = document.querySelectorAll('.btn-add-to-registry[data-registry-id]');
  buttons.forEach((button) => {
    if (button.dataset.registryBound === 'true') {
      return;
    }
    button.dataset.registryBound = 'true';
    button.addEventListener('click', (event) => {
      void addProductToRegistry(event);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindAddToRegistryButtons);
} else {
  bindAddToRegistryButtons();
}

// Quick shop clones PDP buy buttons into the modal after initial bind — rebind on open.
document.addEventListener('quickshop:opened', bindAddToRegistryButtons);

function updateRegistryButtonOnSuccess(button, originalLabel = null) {
  if (!button) {
    return;
  }
  const updateLabel = originalLabel !== null;
  const iconOk = button?.querySelector('.btn-add-to-registry__icon_ok');
  const iconGift = button?.querySelector('.btn-add-to-registry__icon_gift');
  const buttonLabel = button.querySelector('span');

  if (iconOk && iconGift) {
    iconOk.classList.remove('btn-add-to-registry__icon_hidden');
    iconGift.classList.add('btn-add-to-registry__icon_hidden');
    if (buttonLabel && updateLabel) {
      buttonLabel.textContent = REGISTRY_BUTTON_ADDED_TEXT;
    }

    setTimeout(() => {
      iconOk.classList.add('btn-add-to-registry__icon_hidden');
      iconGift.classList.remove('btn-add-to-registry__icon_hidden');
      if (buttonLabel && updateLabel) {
        buttonLabel.textContent = originalLabel;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }, 2000);
  } else {
    if (buttonLabel && updateLabel) {
      buttonLabel.textContent = originalLabel;
    }
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

function handleGridRegistryOpenQuickAdd(event) {
  const button = event.target.closest('[data-grid-registry-opens-quickadd]');
  if (!button || button.disabled || button.classList.contains('out-of-stock')) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const actions = button.closest('.grid-product__actions');
  const quickAddBtn = actions?.querySelector('.quick-add-btn.js-modal-open-quick-add');
  quickAddBtn?.click();
}

async function handleGridRegistryDirectAdd(event) {
  const button = event.target.closest('[data-grid-add-to-registry]');
  if (!button || button.disabled || button.classList.contains('out-of-stock')) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const gridItem = button.closest('.product-grid-item');
  const quickAdd = gridItem?.querySelector('.quick-add-btn[data-id]');
  const variantId = Number(quickAdd?.dataset?.id || button.dataset.variantId);
  const registryId = button.dataset.registryId;
  const visibleBtn = button.querySelector('.grid-product__registry-gift-inner');
  if (!registryId || !variantId) {
    return;
  }
  visibleBtn?.classList.add('btn--loading');
  try {
    const { success } = await postVariantToRegistry(registryId, variantId, 1);
    if (success) {
      updateRegistryButtonOnSuccess(button);
    }
  } catch (error) {
    console.error('handleGridRegistryDirectAdd', error);
  } finally {
    visibleBtn?.classList.remove('btn--loading');
  }
}

document.addEventListener(
  'click',
  (event) => {
    if (event.target.closest('[data-grid-registry-opens-quickadd]')) {
      handleGridRegistryOpenQuickAdd(event);
      return;
    }
    if (event.target.closest('[data-grid-add-to-registry]')) {
      handleGridRegistryDirectAdd(event);
    }
  },
  true,
);
